// DropdownMenu — Radix DropdownMenu wrapper.
// items 배열 또는 children 방식 모두 지원. shortcut 표시 지원.
// Radix가 키보드·ARIA·포커스 트랩을 자동 처리한다.

import {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
  Separator,
  Label,
  type DropdownMenuContentProps,
} from '@radix-ui/react-dropdown-menu';
import { type ReactNode } from 'react';

// ──────────────────────────────────────────────
// Sub-components (children 방식)
// ──────────────────────────────────────────────

export interface DropdownItemProps {
  onSelect?: () => void;
  icon?: ReactNode;
  /** 단축키 표시 (예: "F2", "Del") — 실제 핸들러는 호출자가 직접 등록 */
  shortcut?: string;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  children: ReactNode;
}

/** DropdownMenu 아이템 */
export function DropdownItem({
  onSelect,
  icon,
  shortcut,
  variant = 'default',
  disabled,
  children,
}: DropdownItemProps) {
  return (
    <Item
      className={`ui-dropdown-item${variant === 'danger' ? ' ui-dropdown-item--danger' : ''}`}
      onSelect={onSelect}
      disabled={disabled}
    >
      {icon && <span className="ui-dropdown-item__icon">{icon}</span>}
      <span className="ui-dropdown-item__label">{children}</span>
      {shortcut && <span className="ui-dropdown-item__shortcut">{shortcut}</span>}
    </Item>
  );
}

/** DropdownMenu 구분선 */
export function DropdownSeparator() {
  return <Separator className="ui-dropdown-separator" />;
}

/** DropdownMenu 레이블 (그룹 헤딩) */
export function DropdownLabel({ children }: { children: ReactNode }) {
  return <Label className="ui-dropdown-label">{children}</Label>;
}

// ──────────────────────────────────────────────
// 메인 컨테이너
// ──────────────────────────────────────────────

export interface DropdownMenuProps {
  /** 트리거 요소 (예: <IconButton />) */
  trigger: ReactNode;
  children: ReactNode;
  /** Content 정렬 기준점 */
  align?: DropdownMenuContentProps['align'];
  /** 트리거에서 offset (px) */
  sideOffset?: number;
}

/**
 * Locker UI — DropdownMenu
 * @example
 * <DropdownMenu trigger={<IconButton icon={<MoreHorizontal />} aria-label="더보기" />}>
 *   <DropdownItem onSelect={rename} icon={<Edit />} shortcut="F2">이름 변경</DropdownItem>
 *   <DropdownSeparator />
 *   <DropdownItem onSelect={del} icon={<Trash />} shortcut="Del" variant="danger">삭제</DropdownItem>
 * </DropdownMenu>
 */
export function DropdownMenu({
  trigger,
  children,
  align = 'end',
  sideOffset = 4,
}: DropdownMenuProps) {
  return (
    <Root>
      <Trigger asChild>{trigger}</Trigger>
      <Portal>
        <Content
          className="ui-dropdown-content"
          align={align}
          sideOffset={sideOffset}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  );
}
